/// <amd-module name="Test/TestResultsGrid/Actions/TestResultsGridActionsCreator" />
import {
    FilterByFields,
    IViewContextData,
    TestResultsOutcomeFilterPivots,
    ViewContextStatus,
} from 'Test/common-util/common';
import { TestResultsGroupPivots, FeatureFlagConstants } from 'Test/common-util/Common';
import { FilterState } from 'Test/common-util/Contracts/Filters';
import * as TCMContracts from 'Test/common-util/Contracts/Test';
import { ITestResultTreeData } from 'Test/common-util/Contracts/ViewContracts';
import { FilterHelper } from 'Test/common-util/FilterHelper';
import * as Telemetry from 'Test/common-util/Telemetry';
import * as TelemetryConstants from 'Test/common-util/TelemetryConstants';
import { TestResultsGridActionsHub, ShallowTestCaseResultData } from 'Test/TestResultsGrid/Actions/TestResultsGridActionHub';
import { TestResultGridSource } from 'Test/TestResultsGrid/Sources/TestResultsGridSource';
import { TestResultsStore } from 'Test/TestResultsGrid/Store/TestResultsTreeStore';
import { Constants, FilterSettings } from 'Test/TestResultsGrid/TestResultGridHelper';
import { IVssPageContext } from 'VSS/Platform/Context';
import { isFeatureFlagEnabled } from 'VSS/Platform/Feature';
import { TestManagementServiceName, ITestManagementService } from "Test/Client/TestManagementServices";

export class TestResultsListViewActionCreator {
    constructor(private _resultsActionHub: TestResultsGridActionsHub,
        private _testResultsGridTreeStore: TestResultsStore,
        private _artifactData: IViewContextData,
        private _pageContext: IVssPageContext) {
        this._source = new TestResultGridSource(_pageContext);
    }

    public async initialize(artifactData: IViewContextData): Promise<void> {
        const groupBy = TestResultsGroupPivots.Group_By_Test_Run;
        const filterString = FilterHelper.generateFilterString(FilterHelper.getInitialFilterState());
        const isInProgress = artifactData.status === ViewContextStatus.InProgress;
        this._artifactData = artifactData;
        this._source.resetCache();

        return this._source.getTestResultsByGroupFromDataProvider(artifactData).then((value: TCMContracts.TestResultsDetails) => {
            if (value) {
                // Allow one call to pass through to data provider fetch so that we get group information
                this._resultsActionHub.initializeTestResults.invoke(value);
                if (value.resultsForGroup.length > 0) {
                    this._fetchTestTagSummary(artifactData, groupBy);
                }
                // Verify if the page was opened from a run context
                if(artifactData.runId){
                    this._source.resetCache();
                    // Invoke a filter change so as to cause shallow fetches to occur when page is opened from run context
                    this.onFilterChanged(artifactData, FilterHelper.getInitialFilterState());
                    return;
                }
            } else {
                this._initializeUsingRestApi(groupBy, filterString, isInProgress);
                Telemetry.publishEvent(this._pageContext, this._artifactData, TelemetryConstants.featureTestTab_ErrorInResultsGridDataProvider, { "DataProviderName": "ms.vss-test-web.test-tab-build-resultdetails-data-provider", "error": "No data returned from Dataprovider" });
            }
        }).catch((reason: any) => {
            this._initializeUsingRestApi(groupBy, filterString, isInProgress);
            Telemetry.publishEvent(this._pageContext, this._artifactData, TelemetryConstants.featureTestTab_ErrorInResultsGridDataProvider, { "DataProviderName": "ms.vss-test-web.test-tab-build-resultdetails-data-provider", "error": reason });
        });
    }

    public onInProgressResults() {
        const groupBy = TestResultsGroupPivots.Group_By_Test_Run;
        let filterString = FilterHelper.generateFilterString(FilterHelper.getInitialFilterState());
        this._source.resetCache();

        this._source.getTestResultsByGroup(this._artifactData, groupBy, filterString, true).then(
            response => {
                this._resultsActionHub.initializeTestResults.invoke(response);
                if (response && response.resultsForGroup.length > 0) {
                    this._fetchTestTagSummary(this._artifactData, groupBy);
                }
                if (this._testResultsGridTreeStore.getState().isFilteredOrGrouped) {
                    this._getShallowTestCaseResultsInBatches("");
                }
            },
            reason => {
                this._resultsActionHub.onError.invoke(this._getErrorMessageText(reason));
            }
        );
    }

    // invoked from toolbar via TestResultLeftView.tsx
    public onGroupByChanged(artifactData: IViewContextData, groupBy: TestResultsGroupPivots) {
        let hasMegaFetchStartedInPast: boolean = this._testResultsGridTreeStore.getState().hasMegaFetchStarted;
        this._resultsActionHub.onGroupByChanged.invoke(groupBy);
        this._handleSourceCache(hasMegaFetchStartedInPast);
        this._artifactData = artifactData;
        // if response for resultForGroup empty on GroupBy changed then store will use its cache
        this._source.getTestResultsByGroup(artifactData, groupBy).then((response) => {
            if (response && response.resultsForGroup.length > 0) {
                this._resultsActionHub.groupedResultsFetched.invoke(response);
                this._fetchTestTagSummary(this._artifactData, groupBy);
            }
        },
            (error) => {
                this._resultsActionHub.onError.invoke(this._getErrorMessageText(error));
            }).then(() => {
                this._getShallowTestCaseResultsInBatches(Constants.groupByChangedAction);
            },
                (error) => {
                    this._resultsActionHub.onError.invoke(this._getErrorMessageText(error));
                });
    }

    // invoked from toolbar via TestResultLeftView.tsx
    public async onFilterChanged(artifactData: IViewContextData, filterState: FilterState) {
        if(artifactData.runId){
            let runFilterValue: string[] = [];
            runFilterValue.push(artifactData.runId);
            filterState[FilterByFields.TestRunId] = {
                values: runFilterValue
            }
        }
        this._artifactData = artifactData;
        let hasMegaFetchStartedInPast: boolean = this._testResultsGridTreeStore.getState().hasMegaFetchStarted;
        // Identifying the default filters state to give the better experience.
        // As we don't want to show the batched output for Failed and Aborted Outcomes
        if (this._checkForDefaultFilters(filterState)) {
            this._resultsActionHub.onChangedToDefaultFilters.invoke(filterState);
            this._handleDefaultFilters(filterState);
        } else {
            if (this._isStageFilterSet(filterState) && !this._testResultsGridTreeStore.isStageCacheInitialized()) {
                let tcmService = this._pageContext.getService<ITestManagementService>(TestManagementServiceName);
                let resultGroupDetails: TCMContracts.TestResultsDetails = await tcmService.getTestResultsGroupDetails(+artifactData.data.mainData.id);
                this._resultsActionHub.loadStageData.invoke(resultGroupDetails);
            }
            this._resultsActionHub.filterChanged.invoke(filterState);
            this._handleSourceCache(hasMegaFetchStartedInPast);
            this._getShallowTestCaseResultsInBatches(Constants.filterchangedAction);
        }

        let filterProps: { [key: string]: any } = {
            "OutcomeFilters": filterState["Outcome"],
            "FilterCount": Object.keys(filterState).length,
            "OwnerFilterApplied": filterState["Owner"] ? true : false,
            "ContainerFilterApplied": filterState["AutomatedTestStorage"] ? true : false
        };

        let tagFilterApplied = filterState["Tags"] ? true : false;
        filterProps["TagFilterApplied"] = tagFilterApplied;

        if (tagFilterApplied === true) {
            filterProps["TagFiltersCount"] = Object.keys(filterState["Tags"]).length;
        }

        Telemetry.publishEvent(this._pageContext, this._artifactData, TelemetryConstants.featureTestTab_Filter, filterProps);
    }

    private _initializeUsingRestApi(groupBy: TestResultsGroupPivots, filterString: string, isInProgress: boolean) {
        this._source.getTestResultsByGroup(this._artifactData, groupBy, filterString, isInProgress).then(response => {
            this._resultsActionHub.initializeTestResults.invoke(response);
            console.warn(`TestGrid: initializing from REST API`);
            if (response && response.resultsForGroup.length > 0) {
                this._fetchTestTagSummary(this._artifactData, groupBy);
            }
        }, reason => {
            this._resultsActionHub.onError.invoke(this._getErrorMessageText(reason));
        });
    }

    private _checkForDefaultFilters(filterState: FilterState) {
        return filterState &&
            filterState[FilterByFields.Outcome] &&
            this._testResultsGridTreeStore.getState().groupByField === TestResultsGroupPivots.Group_By_Test_Run &&
            Object.keys(filterState).length == 1 &&
            filterState[FilterByFields.Outcome].values.every(
                filterValue =>
                    filterValue.toString().indexOf(TestResultsOutcomeFilterPivots.Filter_By_Failed) >= 0 ||
                    filterValue.toString().indexOf(TestResultsOutcomeFilterPivots.Filter_By_Aborted) >= 0
            )
    }

    private _isStageFilterSet(filterState: FilterState) {
        if (filterState && filterState[FilterByFields.Stages] && filterState[FilterByFields.Stages].values &&
            filterState[FilterByFields.Stages].values.length)
            return true;

        return false;
    }

    private _handleDefaultFilters(filterState: FilterState) {
        const filterString = FilterHelper.generateFilterString(filterState);

        const isInProgress = this._artifactData.status === ViewContextStatus.InProgress;
        const groupBy = this._testResultsGridTreeStore.getState().groupByField;
        this._initializeUsingRestApi(groupBy, filterString, isInProgress);
    }

    public onColumnsChange(artifactData: IViewContextData, columnTorender: string[]) {
        this._artifactData = artifactData;
        this._resultsActionHub.onGridColumnChanged.invoke(columnTorender);
    }

    public async onFlakyStateChanged() {
        this._resultsActionHub.onMarkTestResultFlaky.invoke();
    }

    public onLoadMoreClick() {
        this._resultsActionHub.onFetchMoreResults.invoke(undefined);
        this._getShallowTestCaseResultsInBatches(Constants.loadMoreAction);
        this._logTelemetryForLoadMoreClick();
    }

    private _logTelemetryForLoadMoreClick() {
        // if feature flag is enabled and the fetched results are less than megaBatchResultsSize then fetch all is being done
        let isMegaFetchFlagEnabled: boolean = isFeatureFlagEnabled(this._pageContext, FeatureFlagConstants.EnableFetchAllResultsUntilMegaBatchSize, false);
        let storeState = this._testResultsGridTreeStore.getState();
        let isFetchAllBeingDone: boolean = (isMegaFetchFlagEnabled && (storeState.fetchedResultsCount < FilterSettings.getMegaResultsSize(this._pageContext))) ? true : false;
        Telemetry.publishEvent(this._pageContext, this._artifactData, TelemetryConstants.featureTestTab_ShowMoreTestResultsAfterFilter, {
            "isAutoFetch": isFetchAllBeingDone,
            "megaBatchSize": FilterSettings.getMegaResultsSize(this._pageContext),
            "shallowResultsBatchSize": isMegaFetchFlagEnabled ? FilterSettings.getNewShallowResultsBatchSize(this._pageContext) : Constants.batchResultsSize
        });
    }

    public onSelectedItemsChanged(testResultTreeData: ITestResultTreeData[]) {
        this._resultsActionHub.onSelectionChange.invoke(testResultTreeData);
    }

    public toggleSort() {
        this._resultsActionHub.sortDataInGridAndToggleState.invoke(undefined);
    }

    // reset the cache if group by changed or filters Outcome changed from failed and aborted
    private _handleSourceCache(hasMegaFetchStartedInPast: boolean) {
        if (!hasMegaFetchStartedInPast && this._testResultsGridTreeStore.getState().hasMegaFetchStarted) {
            this._source.resetCache();
        }
    }

    private _getShallowTestCaseResultsInBatches(action: string): void {
        let filterOutcome: TCMContracts.TestOutcome[] | undefined = undefined;
        if (!this._testResultsGridTreeStore.getState().hasMegaFetchStarted) {
            filterOutcome = [];
            filterOutcome.push(TCMContracts.TestOutcome.Failed);
            filterOutcome.push(TCMContracts.TestOutcome.Aborted);
        }
        let autoFetchResults: boolean = this._testResultsGridTreeStore.getState().fetchedResultsCount <= FilterSettings.getMegaResultsSize(this._pageContext);
        autoFetchResults = isFeatureFlagEnabled(this._pageContext, FeatureFlagConstants.EnableFetchAllResultsUntilMegaBatchSize, false) ? autoFetchResults : false;
        this._source.getShallowTestResultDetails(this._artifactData, action, autoFetchResults, filterOutcome).then(
            (resultsData: ShallowTestCaseResultData) => {
                // Verify if the call was made from a run context
                if (this._artifactData.runId){
                    const runId: string = this._artifactData.runId;
                    // Filter out unwanted run information to prevent them from rendering
                    resultsData.shallowResults = resultsData.shallowResults.filter((shallowResult: TCMContracts.ShallowTestCaseResult) => {
                        return shallowResult.runId == +runId;
                    })
                }
                this._resultsActionHub.shallowResultsFetched.invoke(resultsData);
                if (resultsData.shallowResults && resultsData.shallowResults.length > 0) {
                    return true;
                } else {
                    return false;
                }
        }).then((shouldRenderMore: boolean) => {
            if (shouldRenderMore) {
                this._getShallowTestCaseResultsInBatches("");
            }
            // If no further continuation token is fetched then remove the loading spinner and changed total results count to current count
            else {
                    if (this._artifactData.runId){
                        this._resultsActionHub.handleRunLevelResultCount.invoke();
                }
                let isEnableFilteringMessage: boolean = isFeatureFlagEnabled(this._pageContext, FeatureFlagConstants.EnableFilteringMessage, false);
                if (isEnableFilteringMessage) {
                    if (action == "") {
                        this._resultsActionHub.handleRunSummaryCountMismatch.invoke();
                    }
                }
                else {
                    this._resultsActionHub.handleRunSummaryCountMismatch.invoke();
                }
            }
        }, (error: any) => {
            this._resultsActionHub.onError.invoke(this._getErrorMessageText(error));
        });
    }

    private _getErrorMessageText(errorMessage: any): string {
        if (errorMessage == null) {
            return "";
        } else if (errorMessage.message != null) {
            return errorMessage.message;
        } else if (errorMessage.info != null) {
            return errorMessage.info;
        } else {
            return errorMessage.toString();
        }
    }

    private _fetchTestTagSummary(artifactData: IViewContextData, groupBy: TestResultsGroupPivots): void {
        if (this._getTagsummary && groupBy === TestResultsGroupPivots.Group_By_Test_Run && isFeatureFlagEnabled(this._pageContext, this._tagsForTestRunFF, false)) {
            this._source.getTestTagSummary(artifactData).then((summary) => {
                if (artifactData.status !== ViewContextStatus.InProgress) {
                    this._getTagsummary = false;
                }
                if (summary && summary.tagsGroupByTestArtifact && Object.keys(summary.tagsGroupByTestArtifact).length) {
                    this._resultsActionHub.onTestTagSummaryFetched.invoke(summary);
                }
            }, (error) => {
                this._resultsActionHub.onWarning.invoke("Request for Tags has failed. Please Try Again.");
                Telemetry.publishEvent(this._pageContext, this._artifactData, TelemetryConstants.featureTestTab_ErrorInResultsGridDataProvider, { "DataProviderName": "ms.vss-test-web.test-tab-build-resultdetails-data-provider", "error": "Test Tag API Call failure" });
            });
        }
    }

    private _source: TestResultGridSource;
    private _getTagsummary: boolean = true;
    private readonly _tagsForTestRunFF: string = "TestManagement.Server.TagsInTestRun";
}